import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Badge,
  Button,
  Skeleton,
} from "@repo/ui";
import { formatToApiDate } from "@repo/utils";
import { Link } from "@tanstack/react-router";
import {
  Clock,
  User,
  Building2,
  ArrowRight,
  CalendarClock,
  Inbox,
  UserCircle,
} from "lucide-react";
import { EntityReferenceBadge } from "@/components/reminders/entity-reference-badge";
import type { Reminder } from "@/hooks/use-reminders";

interface DashboardRemindersProps {
  reminders: Reminder[];
  isLoading: boolean;
}

function getDueDateInfo(dueDate: string): {
  label: string;
  isOverdue: boolean;
  isToday: boolean;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");

  const diffMs = due.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      label: `${Math.abs(diffDays)}d overdue`,
      isOverdue: true,
      isToday: false,
    };
  }
  if (diffDays === 0) {
    return { label: "Due today", isOverdue: false, isToday: true };
  }
  if (diffDays === 1) {
    return { label: "Tomorrow", isOverdue: false, isToday: false };
  }
  return { label: `In ${diffDays} days`, isOverdue: false, isToday: false };
}

function RemindersSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="h-4 w-4 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
      ))}
    </div>
  );
}

const PRIORITY_DOT_COLORS: Record<string, string> = {
  urgent: "bg-destructive",
  standard: "bg-warning/60",
};

export function DashboardReminders({
  reminders,
  isLoading,
}: DashboardRemindersProps) {
  const today = formatToApiDate(new Date());

  // Split into overdue, today, and upcoming
  const overdue = reminders.filter((r) => r.due_date < today);
  const dueToday = reminders.filter((r) => r.due_date === today);
  const upcoming = reminders.filter((r) => r.due_date > today);

  // Show overdue first, then today, then fill with upcoming — at most 8
  const displayReminders = [
    ...overdue,
    ...dueToday,
    ...upcoming.slice(0, Math.max(0, 8 - overdue.length - dueToday.length)),
  ].slice(0, 8);

  // Build a concise summary for the card description
  const summaryParts: string[] = [];
  if (overdue.length > 0) summaryParts.push(`${overdue.length} overdue`);
  if (dueToday.length > 0) summaryParts.push(`${dueToday.length} due today`);
  if (upcoming.length > 0) summaryParts.push(`${upcoming.length} upcoming`);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            Reminders
          </CardTitle>
          {!isLoading && (
            <CardDescription>
              {summaryParts.length > 0 ? (
                <span
                  className={
                    overdue.length > 0 ? "text-destructive font-medium" : ""
                  }
                >
                  {summaryParts.join(" · ")}
                </span>
              ) : (
                "No reminders"
              )}
            </CardDescription>
          )}
        </div>
        <Link to="/reminders">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1"
          >
            View All
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <RemindersSkeleton />
        ) : displayReminders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="rounded-full bg-muted p-3 mb-3">
              <Inbox className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              No upcoming reminders
            </p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {displayReminders.map((reminder) => {
              const dateInfo = getDueDateInfo(reminder.due_date);
              const dotColor =
                PRIORITY_DOT_COLORS[reminder.priority] ??
                "bg-muted-foreground/30";

              return (
                <Link
                  key={reminder.id}
                  to="/reminders"
                  search={{ id: reminder.id }}
                  className="flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/40 group"
                >
                  <span
                    className={`h-2 w-2 rounded-full shrink-0 mt-1.5 ${dotColor}`}
                  />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate leading-tight">
                        {reminder.title}
                      </p>
                      {/* Due date badge */}
                      <Badge
                        variant={dateInfo.isOverdue ? "destructive" : "outline"}
                        className={`shrink-0 text-[11px] h-5 px-1.5 gap-1 ${
                          dateInfo.isToday
                            ? "border-warning text-warning"
                            : !dateInfo.isOverdue
                              ? "text-muted-foreground"
                              : ""
                        }`}
                      >
                        <Clock className="h-3 w-3" />
                        {dateInfo.label}
                      </Badge>
                    </div>

                    {reminder.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {reminder.description}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <Badge
                        variant="outline"
                        className="gap-1 text-[11px] h-5 px-1.5"
                      >
                        {reminder.visibility === "pharmacy" ? (
                          <Building2 className="h-3 w-3" />
                        ) : (
                          <User className="h-3 w-3" />
                        )}
                        {reminder.visibility === "pharmacy"
                          ? "Pharmacy"
                          : "Personal"}
                      </Badge>
                      {reminder.patient_name && (
                        <Badge
                          variant="outline"
                          className="gap-1 text-[11px] h-5 px-1.5"
                        >
                          <UserCircle className="h-3 w-3 text-secondary" />
                          <span className="truncate max-w-[120px]">
                            {reminder.patient_name}
                          </span>
                        </Badge>
                      )}
                      {reminder.reference_type && reminder.reference_id && (
                        <EntityReferenceBadge
                          referenceType={reminder.reference_type}
                          referenceId={reminder.reference_id}
                          className="text-[11px]"
                        />
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
